import { Controller } from '@hotwired/stimulus'

// Walks the list that segments-dropdown injects. Focus is the only state, so
// there is nothing to clean up on disconnect.
export default class extends Controller {
  static targets = ['searchInput', 'content']

  declare readonly searchInputTarget: HTMLInputElement
  declare readonly contentTarget: HTMLElement

  // "Up"/"Down" walk the search input and the visible rows as one cycle.
  moveFocus(event: KeyboardEvent): void {
    const step = this.stepFor(event.key)
    if (step === 0) return

    const links = this.visibleRowLinks()
    if (links.length === 0) return

    event.preventDefault()

    // Index 0 of the cycle is the search input, so row i sits at i + 1.
    const cycleLength = links.length + 1
    const current =
      links.indexOf(document.activeElement as HTMLAnchorElement) + 1
    const next = (current + step + cycleLength) % cycleLength

    // focus() alone can scroll the page as well as the list; scroll only the
    // list, and only by the minimum the row needs.
    if (next === 0) {
      this.searchInputTarget.focus({ preventScroll: true })
      return
    }
    links[next - 1].focus({ preventScroll: true })
    links[next - 1].scrollIntoView({ block: 'nearest' })
  }

  // Escape belongs to text-filter, which listens on the search input only.
  // Hand the key back there so a focused row does not swallow it.
  returnToSearch(event: KeyboardEvent): void {
    if (event.key !== 'Escape') return
    if (document.activeElement === this.searchInputTarget) return

    this.searchInputTarget.focus({ preventScroll: true })
    this.searchInputTarget.dispatchEvent(
      new KeyboardEvent('keydown', { key: 'Escape', bubbles: true })
    )
  }

  private stepFor(key: string): -1 | 0 | 1 {
    if (key === 'ArrowDown') return 1
    if (key === 'ArrowUp') return -1

    return 0
  }

  // Assumes text_filter_controller hides a row with inline `style.display`.
  // A class-based hide there makes every row read as visible here, silently.
  private visibleRowLinks(): HTMLAnchorElement[] {
    return Array.from(
      this.contentTarget.querySelectorAll<HTMLAnchorElement>(
        "[data-behavior='segment-item'] a[href]"
      )
    ).filter(
      (link) =>
        link.closest<HTMLElement>("[data-behavior='segment-item']")?.style
          .display !== 'none'
    )
  }
}
